import React, { useState } from 'react';
import {
  Checkbox,
  HiddenFileInput,
  Select,
  TextInput,
} from '../../Common';
import { useNls } from '../../../nls/nlsProvider';
import type { OpsTemplateField } from '../../../types/ops';
import {
  fromLocalDateTimeInput,
  toLocalDateTimeInput,
} from '../../../utils/localDateTimeInput';
import { RecordFieldValue } from './RecordFieldValue';
import { CashCountFieldInput } from './CashCountFieldInput';

export interface RecordFieldInputProps {
  field: OpsTemplateField;
  value: unknown;
  onChange: (nextValue: unknown) => void;
  inputId?: string;
  labelOverride?: string;
  onPhotoSelect?: (fieldKey: string, files: FileList) => void;
  photoUrlsByPath?: Record<string, string>;
  currency?: string | null;
  previousClosingCount?: number | null;
}

export const RecordFieldInput: React.FC<RecordFieldInputProps> = ({
  field,
  value,
  onChange,
  inputId,
  labelOverride,
  onPhotoSelect,
  photoUrlsByPath = {},
  currency,
  previousClosingCount,
}) => {
  const { t } = useNls();
  const id = inputId ?? `ops-rec-field-${field.key}`;
  const ariaLabel = labelOverride ?? field.label;

  const [rawNumText, setRawNumText] = useState<string>(() =>
    value != null ? String(value) : '',
  );
  const [prevKey, setPrevKey] = useState(field.key);
  const [prevValue, setPrevValue] = useState(value);

  if (field.key !== prevKey || !Object.is(value, prevValue)) {
    setPrevKey(field.key);
    setPrevValue(value);
    if (field.type === 'NUMBER' || field.type === 'READING') {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        setRawNumText(String(value));
      } else if (value === null || value === undefined || value === '') {
        setRawNumText('');
      }
    }
  }

  switch (field.type) {
    case 'NUMBER':
    case 'READING': {
      return (
        <TextInput
          id={id}
          data-testid={id}
          aria-label={ariaLabel}
          value={rawNumText}
          onChange={(raw) => {
            setRawNumText(raw);
            if (raw.trim() === '') {
              setPrevValue(null);
              onChange(null);
            } else {
              const num = Number(raw);
              setPrevValue(num);
              onChange(num);
            }
          }}
        />
      );
    }

    case 'CHOICE': {
      return (
        <div data-testid={id}>
          <Select
            id={id}
            ariaLabel={ariaLabel}
            value={typeof value === 'string' ? value : ''}
            onChange={(next) => onChange(next)}
            options={field.options.map((opt) => ({
              value: opt,
              label: opt,
            }))}
          />
        </div>
      );
    }

    case 'YES_NO': {
      const strVal =
        value === true || value === 'YES' || value === 'true'
          ? 'true'
          : value === false || value === 'NO' || value === 'false'
            ? 'false'
            : '';
      return (
        <div data-testid={id}>
          <Select
            id={id}
            label={ariaLabel}
            ariaLabel={ariaLabel}
            value={strVal}
            onChange={(next) => {
              if (next === 'true') onChange(true);
              else if (next === 'false') onChange(false);
              else onChange(null);
            }}
            options={[
              { value: 'true', label: t('NLSOpsYes') },
              { value: 'false', label: t('NLSOpsNo') },
            ]}
          />
        </div>
      );
    }

    case 'DATE_TIME': {
      return (
        <TextInput
          id={id}
          data-testid={id}
          aria-label={ariaLabel}
          type="datetime-local"
          value={toLocalDateTimeInput(value)}
          onChange={(raw) => {
            if (!raw.trim()) {
              onChange(null);
            } else {
              const ms = fromLocalDateTimeInput(raw);
              onChange(ms ?? raw);
            }
          }}
        />
      );
    }

    case 'ACKNOWLEDGE': {
      return (
        <Checkbox
          id={id}
          data-testid={id}
          checked={Boolean(value)}
          onChange={(next) => onChange(next)}
          label={ariaLabel}
        />
      );
    }

    case 'PHOTO': {
      return (
        <div className="opsRegistersFieldGroup" data-testid={id}>
          <label className="opsRegistersPhotoUploadLabel">
            <span>{t('NLSOpsPhotoUploadPrompt')}</span>
            <HiddenFileInput
              testId={`upload-${id}`}
              accept="image/jpeg,image/png,image/webp,image/heic"
              onFiles={(files) => onPhotoSelect?.(field.key, files)}
            />
          </label>
          <RecordFieldValue
            field={field}
            value={value}
            photoUrlsByPath={photoUrlsByPath}
          />
        </div>
      );
    }

    case 'CASH_COUNT': {
      return (
        <CashCountFieldInput
          field={field}
          value={value}
          onChange={(next) => onChange(next)}
          inputId={id}
          currency={currency}
          previousClosingCount={previousClosingCount}
        />
      );
    }

    default: {
      return (
        <TextInput
          id={id}
          data-testid={id}
          aria-label={ariaLabel}
          value={value != null ? String(value) : ''}
          onChange={(raw) => onChange(raw)}
        />
      );
    }
  }
};
